import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slide,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GetSingleOrderDetailsObject } from "../../../@types/DRO/PurchaseOrderLinesResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import { PurchaseOrderHistory } from "../OrderHistory/PurchaseOrderHistory";
import PurchaseOrderItemsTable from "../PurchaseOrderItemsTable";

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

const ViewPurchaseOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const { setLoading } = useLoading();
  const [orderData, setOrderData] =
    useState<GetSingleOrderDetailsObject | null>(null);
  const [loadingError, setLoadingError] = useState(false);
  const [items, setItems] = useState<
    Array<{
      description: string;
      referenceCode?: string;
      quantity: string;
      received: string;
      price: string;
      taxes: string;
      total: string;
      partId: number;
      unitPrice: number;
      tax: number;
      isExisting?: boolean;
    }>
  >([]);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedQuantities, setEditedQuantities] = useState<{
    [key: string]: string;
  }>({});
  const [editedTaxes, setEditedTaxes] = useState<{
    [key: string]: string;
  }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [originalItems, setOriginalItems] = useState<typeof items>([]);

  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedGSTTreatment, setSelectedGSTTreatment] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [orderDeadline, setOrderDeadline] = useState("");
  const [orderType, setOrderType] = useState("OPEN");
  const [orderStatus, setOrderStatus] = useState("");

  // Enhanced notification states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogSeverity, setDialogSeverity] = useState<"success" | "error">(
    "success"
  );
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"cancel" | "close" | null>(null);

  // Helper function to show snackbar notifications
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Helper function to show dialog notifications
  const showDialog = (
    title: string,
    message: string,
    severity: "success" | "error"
  ) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogSeverity(severity);
    setDialogOpen(true);
  };

  // Extract error message from API response
  const extractErrorMessage = (error: any): string => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    } else if (error.response?.data?.error) {
      return error.response.data.error;
    } else if (error.message) {
      return error.message;
    }
    return "An unexpected error occurred. Please try again.";
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!id) {
        setLoadingError(true);
        showSnackbar("Invalid order ID", "error");
        return;
      }

      setLoading(true);
      try {
        const response = await ProductOrderApi.api.getSingleProductOrderDetails(
          parseInt(id)
        );

        const purchaseOrderLines = response.data.purchaseOrderLines || [];

        // Transform the order data
        const transformedData = {
          ...response.data,
          purchaseOrderLines: purchaseOrderLines.map((line) => ({
            ...line,
            part: {
              id: line.partId,
              description: line.partName || "Unknown Part",
              price: line.price,
            },
          })),
        };

        setOrderData(transformedData);

        // Set items from the API response
        const formattedItems = purchaseOrderLines.map((line: any) => ({
          description: line.partName || "",
          referenceCode: line.referenceCode || "",
          quantity: line.quantityOrdered?.toString() || "",
          received: line.quantityReceived?.toString() || "0",
          price:
            line.unitPrice !== null && line.unitPrice !== undefined
              ? `₹ ${line.unitPrice.toFixed(2)}`
              : "₹ 0.00",
          taxes:
            line.tax !== null && line.tax !== undefined
              ? `${line.tax} %`
              : "N/A",
          total:
            line.price !== null && line.price !== undefined
              ? `₹ ${line.price.toFixed(2)}`
              : "₹ 0.00",
          partId: line.partId,
          unitPrice: line.unitPrice || 0,
          tax: line.tax || 0,
          isExisting: true, // Mark as existing item
        }));

        if (formattedItems.length > 0) {
          setItems(formattedItems);
          setOriginalItems(formattedItems); // Store original items for cancel edit
          
          // Initialize edit states
          const quantities: { [key: string]: string } = {};
          const taxes: { [key: string]: string } = {};
          
          purchaseOrderLines.forEach((line: any) => {
            quantities[`item-${line.partId}`] = line.quantityOrdered?.toString() || "";
            taxes[`item-${line.partId}`] = line.tax?.toString() || "0";
          });
          
          setEditedQuantities(quantities);
          setEditedTaxes(taxes);
        }

        // Set other fields from the API response
        setSelectedVendor(transformedData.vendorDetails?.vendorName || "");
        setSelectedGSTTreatment(transformedData.gstTreatment || "");
        setSelectedWarehouse(
          transformedData?.deliveryWareHouseDetails?.deliveryWarehouseName || ""
        );
        setOrderDeadline(transformedData.expectedArrival);
        setOrderType(transformedData.purchaseOrderType || "OPEN");
        setOrderStatus(transformedData.status?.name || "");

        // Set approvers
        if (
          transformedData.approvals &&
          Array.isArray(transformedData.approvals)
        ) {
          setSelectedApprovers(
            transformedData.approvals.map((approver: any) => approver.name)
          );
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        setLoadingError(true);
        const errorMessage = extractErrorMessage(error);
        showSnackbar(errorMessage, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [id, setLoading]);

  const handleCancelOrder = async () => {
    if (!orderData?.id) return;

    setLoading(true);
    try {
      const response = await ProductOrderApi.api.cancelPurchaseOrder(
        orderData.id
      );

      // Extract success message from API response
      const successMessage =
        response?.message || "Purchase order cancelled successfully";

      showDialog("Order Cancelled", successMessage, "success");

      // Navigate after a short delay to allow user to see the message
      setTimeout(() => {
        navigate(`/home${statusFilter ? `?status=${statusFilter}` : ''}`);
      }, 2000);
    } catch (error) {
      console.error("Error cancelling purchase order", error);
      const errorMessage = extractErrorMessage(error);
      showDialog("Cancellation Failed", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePO = async () => {
    if (!orderData?.id) return;

    setLoading(true);
    try {
      const response = await ProductOrderApi.api.closePurchaseOrder(
        orderData.id
      );

      // Extract success message from API response
      const successMessage =
        response?.message || "Purchase order closed successfully";

      showDialog("Order Closed", successMessage, "success");

      // Navigate after a short delay to allow user to see the message
      setTimeout(() => {
        navigate(`/home${statusFilter ? `?status=${statusFilter}` : ''}`);
      }, 2000);
    } catch (error) {
      console.error("Error closing purchase order", error);
      const errorMessage = extractErrorMessage(error);
      showDialog("Closing Failed", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (type: "cancel" | "close") => {
    setActionType(type);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (actionType === "cancel") {
      handleCancelOrder();
    } else if (actionType === "close") {
      handleClosePO();
    }
    setConfirmDialogOpen(false);
    setActionType(null);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setActionType(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (dialogSeverity === "success") {
      // If it was a success, navigate to home
      navigate(`/home${statusFilter ? `?status=${statusFilter}` : ''}`);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const getStatusClass = (status: string): string => {
    const statusClasses: Record<string, string> = {
      DRAFT: "bg-gray-400",
      CREATED: "bg-blue-400",
      COMPLETED: "bg-green-400",
      APPROVED: "bg-yellow-400",
      CANCELED: "bg-red-400",
      CLOSED: "bg-purple-400",
    };
    return statusClasses[status] || "bg-gray-400";
  };

  const handleBack = () => {
    navigate(`/home${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  const shouldShowCancelButton = () => {
    return orderStatus === "CREATED";
  };

  const shouldShowCloseButton = () => {
    return orderStatus === "APPROVED";
  };

  // Edit mode handlers
  const handleEditClick = () => {
    setIsEditMode(true);
    // Store current items as original for potential cancel
    setOriginalItems([...items]);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Restore original items (removes any newly added items)
    setItems([...originalItems]);
    
    // Reset edited values to original values
    if (orderData?.purchaseOrderLines) {
      const quantities: { [key: string]: string } = {};
      const taxes: { [key: string]: string } = {};
      
      orderData.purchaseOrderLines.forEach((line) => {
        quantities[`item-${line.partId}`] = line.quantityOrdered?.toString() || "";
        taxes[`item-${line.partId}`] = line.tax?.toString() || "0";
      });
      
      setEditedQuantities(quantities);
      setEditedTaxes(taxes);
    }
  };

  const handleQuantityChange = (key: string, newValue: string) => {
    if (newValue === "" || /^\d*\.?\d*$/.test(newValue)) {
      setEditedQuantities((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    }
  };

  const handleTaxChange = (key: string, newValue: string) => {
    if (newValue === "" || /^\d*\.?\d*$/.test(newValue)) {
      setEditedTaxes((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    }
  };

  const calculateItemTotal = (partId: number): number => {
    const quantity = parseFloat(editedQuantities[`item-${partId}`]) || 0;
    // Get the original unit price from the items array
    const item = items.find(item => item.partId === partId);
    const price = item?.unitPrice || 0;
    return quantity * price;
  };

  const validateEditedData = (): boolean => {
    for (const item of items) {
      const quantity = parseFloat(editedQuantities[`item-${item.partId}`]) || 0;
      const tax = parseFloat(editedTaxes[`item-${item.partId}`]) || 0;

      if (quantity <= 0) {
        showSnackbar(`Quantity must be greater than 0 for ${item.description}`, "error");
        return false;
      }
      if (tax < 0 || tax > 100) {
        showSnackbar(`Tax must be between 0 and 100 for ${item.description}`, "error");
        return false;
      }
    }
    return true;
  };

  const handleSaveChanges = async () => {
    if (!orderData?.id) return;

    // Validate data
    if (!validateEditedData()) {
      return;
    }

    setIsSaving(true);
    try {
      // Prepare the order lines data
      const orderLines = items.map((item) => ({
        partId: item.partId,
        quantityOrdered: parseInt(editedQuantities[`item-${item.partId}`], 10) || 0,
        price: item.unitPrice, // Use unit price (editable for new items)
        tax: parseFloat(editedTaxes[`item-${item.partId}`]) || 0,
      }));

      const response = await ProductOrderApi.api.editPurchaseOrder(
        orderData.id,
        orderLines
      );

      // Show success message
      showDialog("Purchase Order Updated", "Purchase order has been successfully updated", "success");
      
      // Refresh the data
      const refreshResponse = await ProductOrderApi.api.getSingleProductOrderDetails(
        orderData.id
      );
      
      // Update the state with fresh data
      const purchaseOrderLines = refreshResponse.data.purchaseOrderLines || [];
      const transformedData = {
        ...refreshResponse.data,
        purchaseOrderLines: purchaseOrderLines.map((line) => ({
          ...line,
          part: {
            id: line.partId,
            description: line.partName || "Unknown Part",
            price: line.price,
          },
        })),
      };
      
      setOrderData(transformedData);
      
      // Update items and edit states with fresh data
      const formattedItems = purchaseOrderLines.map((line: any) => ({
        description: line.partName || "",
        referenceCode: line.referenceCode || "",
        quantity: line.quantityOrdered?.toString() || "",
        received: line.quantityReceived?.toString() || "0",
        price:
          line.unitPrice !== null && line.unitPrice !== undefined
            ? `₹ ${line.unitPrice.toFixed(2)}`
            : "₹ 0.00",
        taxes:
          line.tax !== null && line.tax !== undefined
            ? `${line.tax} %`
            : "N/A",
        total:
          line.price !== null && line.price !== undefined
            ? `₹ ${line.price.toFixed(2)}`
            : "₹ 0.00",
        partId: line.partId,
        unitPrice: line.unitPrice || 0,
        tax: line.tax || 0,
      }));
      
      setItems(formattedItems);
      
      // Update edit states with fresh data
      const quantities: { [key: string]: string } = {};
      const taxes: { [key: string]: string } = {};
      
      purchaseOrderLines.forEach((line: any) => {
        quantities[`item-${line.partId}`] = line.quantityOrdered?.toString() || "";
        taxes[`item-${line.partId}`] = line.tax?.toString() || "0";
      });
      
      setEditedQuantities(quantities);
      setEditedTaxes(taxes);
      
      // Exit edit mode
      setIsEditMode(false);
      
    } catch (error) {
      console.error("Error updating purchase order:", error);
      const errorMessage = extractErrorMessage(error);
      showDialog("Update Failed", errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const shouldShowEditButton = () => {
    return orderStatus === "CREATED" || orderStatus === "APPROVED";
  };

  if (loadingError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <ErrorIcon sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
          <h2 className="text-xl font-semibold mb-2">Error loading order</h2>
          <p className="text-gray-600 mb-4">
            Unable to fetch order details. Please try again.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      {/* Heading */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ textAlign: "left", fontWeight: "500", color: "#1E1E1E", mb: 2 }}>
        View Purchase Order
      </Typography>

      {/* Breadcrumbs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to={`/home${statusFilter ? `?status=${statusFilter}` : ''}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to={`/home${statusFilter ? `?status=${statusFilter}` : ''}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Orders
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">View Order</span>
        </div>
      </Box>

      {/* Status and Order Info */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "600" }}>
            Order #{orderData?.id || "N/A"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Created:{" "}
            {orderData?.confirmationDate
              ? new Date(orderData.confirmationDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        </Box>
        <div
          className={`text-white font-semibold rounded-xl px-2 ${getStatusClass(
            orderData.status?.name
          )}`}>
          {orderData.status?.name || "N/A"}
        </div>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Vendor
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              disabled
              value={selectedVendor}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              GST Treatment
            </Typography>
            <TextField
              fullWidth
              value={selectedGSTTreatment}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Deliver To
            </Typography>
            <TextField
              fullWidth
              value={selectedWarehouse}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Approval By
            </Typography>
            <TextField
              fullWidth
              value={selectedApprovers.join(", ")}
              variant="standard"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Order Deadline
            </Typography>
            <TextField
              fullWidth
              value={orderDeadline}
              variant="standard"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{ fontWeight: "500", mb: 1 }}>
              Order Type
            </Typography>
            <TextField
              fullWidth
              value={orderType === "OPEN" ? "Open Order" : "Close Order"}
              variant="standard"
              disabled
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table - using reusable component */}
      <PurchaseOrderItemsTable
        items={items.map((item) => ({
          id: item.partId,
          description: item.description,
          referenceCode: item.referenceCode,
          quantity: isEditMode
            ? (editedQuantities[`item-${item.partId}`] ?? item.quantity)
            : Number(item.quantity),
          received: Number(item.received),
          price: isEditMode && !item.isExisting ? item.price : item.unitPrice,
          tax: isEditMode
            ? (editedTaxes[`item-${item.partId}`] ?? item.tax)
            : item.tax,
          total: Number(item.total?.replace(/[^\d.]/g, "")) || 0,
          isExisting: item.isExisting || false,
        }))}
        onItemChange={(index, field, value) => {
          if (!isEditMode) return;
          const newItems = [...items];
    
          if (field === "description" && !newItems[index].isExisting) {
            // Handle part selection for new items only
            console.log("Selected part value:", value); // Debug log
            
            if (value && value.data) {
              // Update all the item properties from the selected part
              newItems[index].description = value.data.description || value.label || "";
              newItems[index].referenceCode = value.data.referenceCode || "";
              newItems[index].partId = value.data.id; // This is the correct partId from API
              newItems[index].unitPrice = value.data.costPrice || 0;
              newItems[index].price = (value.data.costPrice  || 0).toString();
              
              console.log("Setting partId to:", value.data.id); // Debug log
              
              // Recalculate total with current quantity and tax
              const quantity = parseFloat(newItems[index].quantity) || 1;
              const tax = newItems[index].tax || 0;
              const total = quantity * newItems[index].unitPrice * (1 + tax / 100);
              newItems[index].total = total.toFixed(2);
              
              // Update edited quantities and taxes state
              const key = `item-${value.data.id}`;
              setEditedQuantities(prev => ({ ...prev, [key]: quantity.toString() }));
              setEditedTaxes(prev => ({ ...prev, [key]: tax.toString() }));
            } else {
              // Clear the item data if no selection
              newItems[index].description = "";
              newItems[index].referenceCode = "";
              newItems[index].partId = 0;
              newItems[index].unitPrice = 0;
              newItems[index].price = "0";
              newItems[index].total = "0";
            }
          } else if (field === "quantity") {
            const quantityInput = value.toString();
            if (quantityInput !== "" && !/^\d*$/.test(quantityInput)) {
              return;
            }

            const key = `item-${newItems[index].partId}`;
            setEditedQuantities(prev => ({ ...prev, [key]: quantityInput }));
            
            // Recalculate total
            const price = newItems[index].unitPrice || 0;
            const tax = parseFloat(editedTaxes[key]) || newItems[index].tax || 0;
            const total = (parseInt(quantityInput, 10) || 0) * price * (1 + tax / 100);
            newItems[index].total = total.toFixed(2);
            newItems[index].quantity = quantityInput;
          } else if (field === "tax") {
            const key = `item-${newItems[index].partId}`;
            setEditedTaxes(prev => ({ ...prev, [key]: value.toString() }));
            
            // Recalculate total
            const quantity = parseFloat(editedQuantities[key]) || parseFloat(newItems[index].quantity) || 0;
            const price = newItems[index].unitPrice || 0;
            const total = quantity * price * (1 + (parseFloat(value) || 0) / 100);
            newItems[index].total = total.toFixed(2);
            newItems[index].tax = parseFloat(value) || 0;
          } else if (field === "price" && !newItems[index].isExisting) {
            // Handle price change for new items only
            const priceInput = value.toString();
            if (priceInput !== "" && !/^\d*\.?\d*$/.test(priceInput)) {
              return;
            }

            const priceValue = parseFloat(priceInput);
            newItems[index].unitPrice = Number.isNaN(priceValue) ? 0 : priceValue;
            newItems[index].price = priceInput;
            
            // Recalculate total
            const key = `item-${newItems[index].partId}`;
            const quantity = parseFloat(editedQuantities[key]) || parseFloat(newItems[index].quantity) || 0;
            const tax = parseFloat(editedTaxes[key]) || newItems[index].tax || 0;
            const total = quantity * (Number.isNaN(priceValue) ? 0 : priceValue) * (1 + tax / 100);
            newItems[index].total = total.toFixed(2);
          }
          
          setItems(newItems);
        }}
        onAddItem={() => {
          if (!isEditMode) return;
          const newPartId = Date.now(); // Use timestamp as temporary ID for new items
          setItems([
            ...items,
            {
              description: "",
              referenceCode: "",
              quantity: "1",
              received: "0",
              price: "0",
              taxes: "0",
              total: "0",
              partId: newPartId,
              unitPrice: 0,
              tax: 0,
              isExisting: false, // Mark as new item
            },
          ]);
          // Initialize edit states for new item
          setEditedQuantities(prev => ({
            ...prev,
            [`item-${newPartId}`]: "1"
          }));
          setEditedTaxes(prev => ({
            ...prev,
            [`item-${newPartId}`]: "0"
          }));
        }}
        onDeleteItem={(index) => {
          if (!isEditMode) return;
          const itemToDelete = items[index];
          // Only allow deletion of new items (not existing ones)
          if (!itemToDelete.isExisting) {
            setItems(items.filter((_, i) => i !== index));
            // Clean up edit states
            const newEditedQuantities = { ...editedQuantities };
            const newEditedTaxes = { ...editedTaxes };
            delete newEditedQuantities[`item-${itemToDelete.partId}`];
            delete newEditedTaxes[`item-${itemToDelete.partId}`];
            setEditedQuantities(newEditedQuantities);
            setEditedTaxes(newEditedTaxes);
          }
        }}
        fetchPartOptions={async (page, search) => {
          try {
            const res = await AllPartApi.api.getPartList(page, 10, search);
            console.log("API response:", res.data.data); // Debug log
            
            return {
              options: res.data.data.map((part: any) => ({
                value: part.id.toString(),
                label: part.description,
                data: {
                  id: part.id, // This should be the correct partId
                  description: part.description,
                  referenceCode: part.referenceCode || "",
                  salesPrice: part.salesPrice || 0,
                  costPrice: part.costPrice || 0,
                  // Include any other part properties you need
                },
              })),
              hasMore: res.data.data.length === 10,
            };
          } catch (error) {
            console.error("Error fetching parts:", error);
            return { options: [], hasMore: false };
          }
        }}
        editable={isEditMode}
      />

      {/* Show Total Amount below the table */}
      <Box sx={{ mt: 2, textAlign: "right" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "600" }}>
          Total Amount: ₹{" "}
          {orderData?.totalAmount !== undefined
            ? Number(orderData.totalAmount).toFixed(2)
            : "0.00"}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          marginTop: 4,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => setHistoryDialogOpen(true)}
          sx={{
            padding: "10px 20px",
            borderRadius: "32px",
            textTransform: "none",
            fontWeight: "500",
          }}>
          History
        </Button>

        {!isEditMode && shouldShowCancelButton() && (
          <Button
            variant="contained"
            color="error"
            onClick={() => handleActionClick("cancel")}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Cancel Order
          </Button>
        )}

        {!isEditMode && shouldShowEditButton() && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleEditClick}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
            }}>
            Edit
          </Button>
        )}

        {isEditMode && (
          <>
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveChanges}
              disabled={isSaving}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
                minWidth: "120px",
              }}
              startIcon={
                isSaving ? (
                  <span
                    className="loader"
                    style={{
                      width: 18,
                      height: 18,
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : null
              }>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelEdit}
              disabled={isSaving}
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
              }}>
              Cancel Edit
            </Button>
          </>
        )}

        {!isEditMode && shouldShowCloseButton() && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleActionClick("close")}
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              textTransform: "none",
              fontWeight: "500",
              backgroundColor: "#6B46C1",
              "&:hover": {
                backgroundColor: "#553C9A",
              },
            }}>
            Close PO
          </Button>
        )}

        <Button
          variant="outlined"
          color="primary"
          onClick={handleBack}
          sx={{
            padding: "10px 20px",
            borderRadius: "32px",
            textTransform: "none",
            fontWeight: "500",
          }}>
          Back to Orders
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        TransitionComponent={Transition}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description">
        <DialogTitle id="confirm-dialog-title">
          {actionType === "cancel"
            ? "Cancel Purchase Order"
            : "Close Purchase Order"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {actionType === "cancel"
              ? "Are you sure you want to cancel this purchase order? This action cannot be undone."
              : "Are you sure you want to close this purchase order? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            No, Keep Order
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={actionType === "cancel" ? "error" : "secondary"}
            variant="contained"
            autoFocus>
            {actionType === "cancel" ? "Yes, Cancel Order" : "Yes, Close Order"}
          </Button>
        </DialogActions>
      </Dialog>

      <PurchaseOrderHistory
        purchaseOrderId={orderData?.id ?? 0}
        variant="dialog"
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />

      {/* Enhanced Result Dialog with Icons */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
        aria-labelledby="result-dialog-title"
        aria-describedby="result-dialog-description"
        maxWidth="sm"
        fullWidth>
        <DialogTitle
          id="result-dialog-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1,
          }}>
          {dialogSeverity === "success" ? (
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
          ) : (
            <ErrorIcon sx={{ color: "error.main", fontSize: 28 }} />
          )}
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="result-dialog-description"
            sx={{
              color:
                dialogSeverity === "success" ? "text.primary" : "error.main",
              fontSize: "16px",
            }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="contained"
            sx={{ minWidth: 100 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for quick notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            boxShadow: 3,
            "& .MuiAlert-icon": {
              fontSize: 26,
            },
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default ViewPurchaseOrder;